import React, { useContext, useState, useCallback, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import { TableVirtuoso } from "react-virtuoso";
import { Box, Typography } from "../../../design-system";
import { colors } from "../../../design-system/colors";
import { messageContext } from "../../../../stores/messages/messageContext";
import { autoDestinationUrl, getMyCreationsUrl, stitchCreationsUrl } from "../../../constants";
import { EXPORTS_EMPTY_STATE_MESSAGE } from "../constants/myLibraryText";
import { CreationData } from "../types";
import VideoThumbnail from "../components/VideoThumbnail";
import InlineVideoPlayer from "../components/InlineVideoPlayer";
import { exportsTableHeader } from "../constants/exportsTableHeader";
import { formatDateAdded } from "../../../../utils/dateFormatting";
import RowActions from "../components/RowActions";
import AutoPostButton from "../components/AutoPostButton";
import { useLibraryLook } from "../looks/libraryLook";
import { TOOLS_SLOT_ID } from "../looks/LibraryShell";
import { EXPORTS_PAPER } from "../looks/libraryLookText";
import SubtitleText from "../looks/SubtitleText";
import RenameDialog from "../components/RenameDialog";
import LibraryTableHeader from "../components/LibraryTableHeader";
import {
  LibraryTable,
  LibraryTableRow,
} from "../components/LibraryTableComponents";
import { ConfirmModal } from "../../../design-system";
import {
  DELETE_CONFIRM_TITLE,
  getDeleteConfirmMessage,
  BUTTON_CONFIRM,
  BUTTON_CANCEL,
  SUCCESS_COPY_TO_CLIPBOARD,
  ERROR_COPY_TO_CLIPBOARD_FAILED,
  CURSOR_POINTER,
  EXPORT_PROCESSING_TEXT,
  EXPORT_GIVE_UP_LABEL,
  EXPORT_GIVE_UP_TITLE,
  EXPORT_GIVE_UP_CONFIRM,
  EXPORT_NO_CHERRY,
  EXPORT_FAILED_TEXT,
  EXPORT_CANCELLED_TEXT,
  EXPORT_DELETE_LABEL,
  LIBRARY_SEARCH_PLACEHOLDER,
  LIBRARY_NO_MATCHES,
  LIBRARY_SORT_LABEL,
} from "../constants/myLibraryText";
import { cancelCreation } from "../utils/cancelCreation";
import { exportKindLabel, exportKindColor, exportSettingsLabel } from "../utils/exportKind";
import { deleteCreation } from "../utils/deleteCreation";
import { renameCreation } from "../utils/renameCreation";
import { useFileDownload } from "../hooks/useFileDownload";
import apiRequest from "../../../../utils/apiClient";
import { useLibraryTabData } from "../hooks/useLibraryTabData";
import { useTableSort } from "../hooks/useTableSort";
import { useDeleteModal } from "../hooks/useDeleteModal";
import LibraryTabLayout from "../components/LibraryTabLayout";
import "./UploadsTab.css";

const exportsColumnWidths = [
  "42px",           // checkbox
  ...exportsTableHeader.map((col) =>
    typeof col.width === "number" ? `${col.width}px` : (col.width as string)
  ),
];

const ExportsVirtuosoTable: React.FC<React.ComponentProps<"table">> = (props) => (
  <LibraryTable {...props}>
    <colgroup>
      {exportsColumnWidths.map((w, i) => (
        <col key={i} style={{ width: i === 1 ? "var(--cc-clipw, 138px)" : w }} />
      ))}
    </colgroup>
    {props.children}
  </LibraryTable>
);

// The virtualized <video> restarts every few seconds otherwise. react-virtuoso REMOUNTS the
// whole row subtree whenever the `components` prop changes identity — and an inline object
// (with an inline TableRow) is a new identity on EVERY render, so each 5s "still processing"
// poll re-render remounted every row, remounting the <video> and re-firing its open-on-mount
// autoplay (clip jumps back to 0, can't stay paused, never finishes). Define the components
// object ONCE at module scope (per react-virtuoso docs) and read row state via the `context`
// prop, so a re-render no longer remounts the player.
const ExportsVirtuosoTableRow = ({ context: ctx, item, ...props }: any) => (
  <tr
    {...props}
    className={`responsive-table-row uploads-table-row${(item as CreationData)?.id === ctx?.newlyStitchedId ? " newly-stitched-row" : ""}`}
  />
);
const EXPORTS_VIRTUOSO_COMPONENTS = {
  Table: ExportsVirtuosoTable,
  TableRow: ExportsVirtuosoTableRow,
} as React.ComponentProps<typeof TableVirtuoso>["components"];

const StitchBanner = memo(({ title, onDismiss }: { title: string; onDismiss: () => void }) => (
  <Box sx={{ background: "#0f2744", borderBottom: "1px solid #1d4ed8", padding: "10px 24px", display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
    <Box sx={{ width: "16px", height: "16px", borderRadius: "50%", border: "2px solid #3b82f6", borderTopColor: "transparent", animation: "spin 0.9s linear infinite", flexShrink: 0 }} />
    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", color: "#93c5fd", flex: 1 }}>
      Stitching <strong>"{title}"</strong> — usually takes 1–2 minutes. It will appear here automatically.
    </Typography>
    <Box
      onClick={onDismiss}
      sx={{ color: "#4b7ab5", fontSize: "18px", lineHeight: 1, cursor: "pointer", padding: "0 4px", "&:hover": { color: "#93c5fd" } }}
      title="Dismiss"
    >
      ×
    </Box>
  </Box>
));

// An export with no file yet is processing — unless the backend says it failed / was given up on.
const isNoCherry = (c: CreationData): boolean => !c.ext && (c.status === "failed" || c.status === "cancelled");
// The 📤 Post button only makes sense on a finished video (not audio or subtitles).
const POSTABLE_EXTS = new Set(["mp4", "mov", "webm", "gif"]);
const isPostable = (c: CreationData): boolean => POSTABLE_EXTS.has((c.ext || "").toLowerCase());
const isProcessing = (c: CreationData): boolean => !c.ext && !isNoCherry(c);

// How the export was made ("6 words · +80 ms breathing room · … · Original") — its own line
// under the title, free to wrap. (Squeezed onto the title's line it crushed the title into a
// one-word-wide column.)
const SettingsLine = memo(({ kind, settings }: { kind?: string; settings?: CreationData["settings"] }) => {
  const detail = exportSettingsLabel(kind, settings);
  if (!detail) return null;
  return (
    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#9a9a9a", lineHeight: "18px" }}>{detail}</Typography>
  );
});

// The export-type tag ("✂ Supercut", "GIF"...).
const KindTag = memo(({ kind, ext }: { kind?: string; ext?: string; settings?: CreationData["settings"] }) => {
  const label = exportKindLabel(kind, ext);
  if (!label) return null;
  return (
    <Box component="span" sx={{ fontFamily: "Outfit, sans-serif", fontSize: "10px", fontWeight: 700, color: "#fff", background: exportKindColor(kind, ext), borderRadius: "4px", px: "6px", py: "1px", letterSpacing: "0.3px", textTransform: "uppercase", flexShrink: 0, whiteSpace: "nowrap" }}>
      {label}
    </Box>
  );
});

// The "no cherry" stamp: the cherry, crossed out, where the thumbnail would be.
const NoCherryStamp = memo(({ width, height }: { width: number | string; height: number | string }) => (
  <Box sx={{ width, height, borderRadius: "8px", background: "#140b0d", border: "1px dashed #5a2a32", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", position: "relative", overflow: "hidden" }}>
    <Box sx={{ position: "relative", fontSize: "24px", lineHeight: 1 }}>
      <Box component="span" sx={{ filter: "grayscale(0.6)", opacity: 0.7 }}>🍒</Box>
      <Box sx={{ position: "absolute", left: "-6px", right: "-6px", top: "50%", height: "3px", background: "#e53935", transform: "rotate(-35deg)", borderRadius: "2px" }} />
    </Box>
    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", fontWeight: 700, color: "#e57373", textTransform: "uppercase", letterSpacing: "0.5px" }}>{EXPORT_NO_CHERRY}</Typography>
  </Box>
));

type ExportSort = "newest" | "oldest" | "longest" | "shortest" | "title" | "type";
const EXPORT_SORT_OPTIONS: { key: ExportSort; label: string }[] = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "longest", label: "Longest" },
  { key: "shortest", label: "Shortest" },
  { key: "title", label: "Title A–Z" },
  { key: "type", label: "Type" },
];

// A clip's length with decimals — "2.84s", "13.7s", "1m 5.2s" — so short supercuts read true.
const fmtLength = (secs?: number | null): string => {
  if (!secs) return "—";
  if (secs < 10) return `${secs.toFixed(2)}s`;
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const m = Math.floor(secs / 60);
  return `${m}m ${(secs % 60).toFixed(1)}s`;
};

// Format a seconds value as "18s" / "1m 5s"; falsy/zero shows an em dash.
const fmtSecs = (secs?: number | null): string => {
  if (!secs) return "—";
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const ExportsTab: React.FC = (): JSX.Element => {
  const messageStore = useContext(messageContext);
  // Gallery / Strips: the light page draws Exports as picture cards (below); Classic keeps the table.
  const { look } = useLibraryLook();
  const [toolsSlot, setToolsSlot] = useState<HTMLElement | null>(null);
  const [paperView, setPaperViewState] = useState<"cards" | "list">(() => (localStorage.getItem("cc_exports_view_light") === "list" ? "list" : "cards"));
  const setPaperView = (v: "cards" | "list") => { setPaperViewState(v); localStorage.setItem("cc_exports_view_light", v); };
  const [paperCardW, setPaperCardW] = useState<number>(() => Number(localStorage.getItem("cc_exports_cardw_light")) || 280);
  useEffect(() => { localStorage.setItem("cc_exports_cardw_light", String(paperCardW)); }, [paperCardW]);
  useEffect(() => { setToolsSlot(look === "classic" ? null : document.getElementById(TOOLS_SLOT_ID)); }, [look]);

  const getCreationSortValue = useCallback(
    (creation: CreationData, field: string): string | number => {
      if (field === "name_description") return creation.title?.toLowerCase() || "";
      if (field === "date_added") return new Date(creation.created).getTime();
      if (field === "clip_title") return creation.title?.toLowerCase() || "";
      if (field === "duration") return (creation as any).duration_seconds || 0;
      if (field === "processing_time") return (creation as any).processing_time_seconds || 0;
      if (field === "file_type") return exportKindLabel(creation.kind, creation.ext).toLowerCase();
      return "";
    },
    [],
  );

  const { items: creations, setItems: setCreations, loading, error, silentRefetch } =
    useLibraryTabData<CreationData>(getMyCreationsUrl, messageStore, "creations");

  // Pin each creation's presigned video URL to the first non-empty value we see for that id.
  // The 5s "still processing" poll below refetches the whole list, and the backend hands out
  // a FRESH presigned URL every time — so without pinning, a playing <video>'s src changed
  // every 5s and the element reloaded, making clips restart endlessly (never able to finish).
  // A placeholder row has no URL yet; once its real URL arrives it's pinned and stays stable.
  const stableVideoUrls = useRef<Map<number, string>>(new Map());
  const stableCreations = creations.map((c) => {
    if (!c.video) return c;
    if (!stableVideoUrls.current.has(c.id)) stableVideoUrls.current.set(c.id, c.video);
    return { ...c, video: stableVideoUrls.current.get(c.id) as string };
  });

  const { sortField, sortDirection, handleSortClick, sortedData: sortedCreations } =
    useTableSort<CreationData>(stableCreations, getCreationSortValue);
  const { deleteModalOpen, itemToDelete: creationToDelete, isDeleting, handleDeleteClick, handleDeleteConfirm, handleDeleteModalClose } =
    useDeleteModal(
      deleteCreation,
      messageStore,
      (id) => {
        setCreations((prev) => prev.filter((c) => c.id !== id));
        setBasketIds((prev) => prev.filter((bid) => bid !== id));
      },
    );

  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [creationToRename, setCreationToRename] = useState<{
    id: number; title: string; description: string;
  } | null>(null);
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [activeCreationId, setActiveCreationId] = useState<number | null>(null);

  // Clip Basket state
  const [basketIds, setBasketIds] = useState<number[]>([]);
  const [stitchTitle, setStitchTitle] = useState<string>("");
  const [stitching, setStitching] = useState<boolean>(false);
  const [batchDownloading, setBatchDownloading] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);

  // Stitch progress tracking
  const [pendingStitch, setPendingStitch] = useState<string | null>(null);
  const [newlyStitchedId, setNewlyStitchedId] = useState<number | null>(null);
  const prevCreationCount = useRef<number>(0);

  // Poll every 5s while a stitch is in progress
  useEffect(() => {
    if (!pendingStitch) return;
    const interval = setInterval(() => silentRefetch(), 5000);
    return () => clearInterval(interval);
  }, [pendingStitch, silentRefetch]);

  // Poll while any export is still being created. A placeholder row has no file
  // extension yet (empty `ext`) — once the worker finishes it flips to mp4/srt/etc.
  const anyProcessing = creations.some(isProcessing);
  useEffect(() => {
    if (!anyProcessing) return;
    const interval = setInterval(() => silentRefetch(), 5000);
    return () => clearInterval(interval);
  }, [anyProcessing, silentRefetch]);

  // --- View preferences (persisted across refreshes) ---
  const [viewMode, setViewMode] = useState<"list" | "grid">(
    () => (localStorage.getItem("cc_exports_view") as "list" | "grid") || "list",
  );
  const [cardWidth, setCardWidth] = useState<number>(
    () => Number(localStorage.getItem("cc_exports_cardw")) || 240,
  );
  const [categoryFilter, setCategoryFilter] = useState<"all" | "auto" | "manual">(
    () => (localStorage.getItem("cc_exports_cat") as "all" | "auto" | "manual") || "all",
  );
  useEffect(() => { localStorage.setItem("cc_exports_view", viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem("cc_exports_cardw", String(cardWidth)); }, [cardWidth]);
  useEffect(() => { localStorage.setItem("cc_exports_cat", categoryFilter); }, [categoryFilter]);
  const [searchText, setSearchText] = useState<string>("");
  const [sortChoice, setSortChoice] = useState<ExportSort>(
    () => (localStorage.getItem("cc_exports_sort") as ExportSort) || "newest",
  );
  useEffect(() => { localStorage.setItem("cc_exports_sort", sortChoice); }, [sortChoice]);

  // Stop any inline-playing clip when the list re-filters / re-sorts / switches view.
  // Otherwise that row remounts and the open-on-mount autoplay replays the last clip
  // you watched — which looked like "clicking a filter auto-plays a video".
  useEffect(() => { setActiveCreationId(null); }, [categoryFilter, viewMode, sortField, sortDirection, sortChoice, searchText]);

  // An export is a CherryClipz automated cut if the backend flagged it, OR (for
  // rows created before that flag existed) its title carries the 🍒 prefix.
  const isAutoCut = (c: CreationData): boolean =>
    Boolean(c.auto_generated) || /^\s*🍒/.test(c.title || "");

  // Category filter: All / CherryClipz (one-click automated cuts) / Manual exports,
  // then the search box (title, description or type), then the Sort menu. "Newest" keeps
  // the server order, so the column-header sorting above still applies.
  const query = searchText.trim().toLowerCase();
  const filteredCreations = sortedCreations.filter((c) => {
    if (categoryFilter === "auto" && !isAutoCut(c)) return false;
    if (categoryFilter === "manual" && isAutoCut(c)) return false;
    if (query) {
      const hay = `${c.title || ""} ${c.description || ""} ${exportKindLabel(c.kind, c.ext)}`.toLowerCase();
      if (!hay.includes(query)) return false;
    }
    return true;
  });
  if (sortChoice !== "newest") {
    const time = (c: CreationData) => new Date(c.created).getTime();
    const cmp: Record<ExportSort, (a: CreationData, b: CreationData) => number> = {
      newest: () => 0,
      oldest: (a, b) => time(a) - time(b),
      longest: (a, b) => (b.duration_seconds || 0) - (a.duration_seconds || 0),
      shortest: (a, b) => (a.duration_seconds || 0) - (b.duration_seconds || 0),
      title: (a, b) => (a.title || "").localeCompare(b.title || ""),
      type: (a, b) => exportKindLabel(a.kind, a.ext).localeCompare(exportKindLabel(b.kind, b.ext)),
    };
    filteredCreations.sort(cmp[sortChoice]);
  }

  // CherryClipz Auto: with a webhook set up, each finished video gets a 📤 Post button.
  const [hasAuto, setHasAuto] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    apiRequest(autoDestinationUrl, { method: "GET" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled) setHasAuto(Boolean(data?.destination?.url && data?.destination?.enabled));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const markPosted = (id: number, state: "sent" | "failed") =>
    setCreations((prev) => prev.map((c) => (c.id === id ? { ...c, auto_state: state } : c)));
  const showPostMessage = (text: string, tone: "success" | "error") => messageStore.setMessage(text, tone, 6000);

  const handleGiveUp = async (creation: CreationData): Promise<void> => {
    if (!window.confirm(EXPORT_GIVE_UP_CONFIRM)) return;
    if (await cancelCreation(creation.id, messageStore)) {
      setCreations((prev) => prev.map((c) => (c.id === creation.id ? { ...c, status: "cancelled" } : c)));
    }
  };

  // Thumbnail sizing shared by both views (slider-driven).
  const listThumbHeight = Math.round((cardWidth * 9) / 16);

  // Auto-clear banner after 5 minutes (in case worker fails silently)
  useEffect(() => {
    if (!pendingStitch) return;
    const timeout = setTimeout(() => setPendingStitch(null), 5 * 60 * 1000);
    return () => clearTimeout(timeout);
  }, [pendingStitch]);

  // Detect when the stitched clip arrives
  useEffect(() => {
    if (pendingStitch && creations.length > prevCreationCount.current) {
      const newest = [...creations].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0];
      if (newest) {
        setNewlyStitchedId(newest.id);
        setTimeout(() => setNewlyStitchedId(null), 4000);
      }
      setPendingStitch(null);
    }
    prevCreationCount.current = creations.length;
  }, [creations.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const { download, isDownloadingId } = useFileDownload({
    onError: (msg) => messageStore.setMessage(msg, "error"),
  });

  const isSubtitleFile = (ext: string): boolean => ["vtt", "srt"].includes(ext?.toLowerCase());
  const isZipFile = (ext: string): boolean => ext?.toLowerCase() === "zip";
  // A GIF shows as an (animated) image — a <video> player can't play it and it can't be stitched.
  const isGifFile = (ext: string): boolean => ext?.toLowerCase() === "gif";
  const isVideoFile = (ext: string): boolean => !isSubtitleFile(ext) && !isZipFile(ext) && !isGifFile(ext);

  const handleClipClick = (creation: CreationData): void => {
    if (!isVideoFile(creation.ext)) return;
    setActiveCreationId((prev) => (prev === creation.id ? null : creation.id));
  };

  const handleCopyToClipboard = async (url: string): Promise<void> => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch file");
      const text = await response.text();
      await navigator.clipboard.writeText(text);
      messageStore.setMessage(SUCCESS_COPY_TO_CLIPBOARD, "success");
    } catch {
      messageStore.setMessage(ERROR_COPY_TO_CLIPBOARD_FAILED, "error");
    }
  };

  const handleRenameClick = (creationId: number, currentTitle: string, currentDescription: string): void => {
    setCreationToRename({ id: creationId, title: currentTitle, description: currentDescription ?? "" });
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async (newTitle: string, newDescription?: string): Promise<void> => {
    if (!creationToRename) { setRenameDialogOpen(false); setCreationToRename(null); return; }
    const titleChanged = newTitle.trim() !== creationToRename.title;
    const descriptionChanged = newDescription !== undefined && newDescription !== creationToRename.description;
    if (titleChanged || descriptionChanged) {
      setIsRenaming(true);
      const success = await renameCreation(creationToRename.id, newTitle.trim(), messageStore, newDescription);
      setIsRenaming(false);
      if (success) {
        setCreations((prev) =>
          prev.map((c) =>
            c.id === creationToRename.id
              ? { ...c, title: newTitle.trim(), description: newDescription !== undefined ? newDescription : c.description }
              : c,
          ),
        );
        setRenameDialogOpen(false);
        setCreationToRename(null);
      }
    } else {
      setRenameDialogOpen(false);
      setCreationToRename(null);
    }
  };

  const MAX_STITCH = 10;

  const toggleBasket = (id: number, ext: string): void => {
    if (!isVideoFile(ext)) return;
    setBasketIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_STITCH) return prev;
      return [...prev, id];
    });
  };

  const removeFromBasket = (id: number): void => {
    setBasketIds((prev) => prev.filter((x) => x !== id));
  };

  const reorderBasket = (fromIdx: number, toIdx: number): void => {
    setBasketIds((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  };

  const handleStitch = async (): Promise<void> => {
    if (basketIds.length < 2) return;
    const title = stitchTitle.trim() || "Stitched Video";
    setStitching(true);
    try {
      const resp = await apiRequest(stitchCreationsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ creation_ids: basketIds, title }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || "Stitch failed");
      }
      messageStore.setMessage(`"${title}" is queued — watch for it below.`, "success");
      setPendingStitch(title);
      prevCreationCount.current = creations.length;
      setBasketIds([]);
      setStitchTitle("");
    } catch (e: any) {
      messageStore.setMessage(e.message || "Failed to stitch clips", "error");
    } finally {
      setStitching(false);
    }
  };

  const handleDownloadAll = async (): Promise<void> => {
    if (basketCreations.length === 0 || batchDownloading) return;
    setBatchDownloading(true);
    for (const creation of basketCreations) {
      await download(creation.id, creation.video, creation.title, creation.ext || "mp4");
    }
    setBatchDownloading(false);
  };

  const basketCreations = basketIds
    .map((id) => creations.find((c) => c.id === id))
    .filter(Boolean) as CreationData[];

  const dialogs = (
    <>
      <ConfirmModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        title={DELETE_CONFIRM_TITLE}
        message={creationToDelete ? getDeleteConfirmMessage(creationToDelete.title) : ""}
        confirmLabel={BUTTON_CONFIRM}
        cancelLabel={BUTTON_CANCEL}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteModalClose}
        loading={isDeleting}
      />
      <RenameDialog
        open={renameDialogOpen}
        currentTitle={creationToRename?.title || ""}
        currentDescription={creationToRename?.description ?? ""}
        onClose={() => { setRenameDialogOpen(false); setCreationToRename(null); }}
        onConfirm={handleRenameConfirm}
        loading={isRenaming}
      />
    </>
  );

  if (look !== "classic") {
    const tools = (
      <>
        <div className="ico" title={EXPORTS_PAPER.categoryTitle}>
          {(["all", "auto", "manual"] as const).map((key) => (
            <button type="button" key={key} className={categoryFilter === key ? "on" : ""} onClick={() => setCategoryFilter(key)}>
              {EXPORTS_PAPER.category[key]}
            </button>
          ))}
        </div>
        <label className="find">
          <span aria-hidden>🔍</span>
          <input type="search" value={searchText} placeholder={EXPORTS_PAPER.find} onChange={(e) => setSearchText(e.target.value)} />
        </label>
        <select value={sortChoice} onChange={(e) => setSortChoice(e.target.value as ExportSort)} aria-label={LIBRARY_SORT_LABEL}>
          {EXPORT_SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <div className="ico">
          <button type="button" className={paperView === "cards" ? "on" : ""} onClick={() => setPaperView("cards")}>▦ {EXPORTS_PAPER.cards}</button>
          <button type="button" className={paperView === "list" ? "on" : ""} onClick={() => setPaperView("list")}>☰ {EXPORTS_PAPER.list}</button>
        </div>
        {paperView === "cards" && (
          <input type="range" min={220} max={440} step={10} value={paperCardW} aria-label={EXPORTS_PAPER.size} title={EXPORTS_PAPER.size}
            onChange={(e) => setPaperCardW(Number(e.target.value))} style={{ width: 80, accentColor: "#e61d50" }} />
        )}
      </>
    );
    return (
      <div>
        {toolsSlot && createPortal(tools, toolsSlot)}
        {loading && <div className="hint">…</div>}
        {!loading && error && <div className="hint">{error}</div>}
        {!loading && !error && creations.length === 0 && <div className="hint">{EXPORTS_EMPTY_STATE_MESSAGE}</div>}
        {pendingStitch && <div className="xbanner">✂ {EXPORTS_PAPER.stitching(pendingStitch)}</div>}

        {basketCreations.length > 0 && (
          <div className="basket xbasket">
            <div className="b-hd">
              <b>{EXPORTS_PAPER.basket}</b>
              <span className="meta">{EXPORTS_PAPER.basketNote(basketCreations.length, MAX_STITCH)}</span>
              <button type="button" className="x" aria-label={EXPORTS_PAPER.clear} onClick={() => setBasketIds([])}>✕</button>
            </div>
            <div className="b-line">
              {basketCreations.map((creation, idx) => (
                <div
                  key={creation.id}
                  className="b-item"
                  style={{ width: 150 }}
                  draggable
                  onDragStart={() => setDragOver(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragOver !== null) reorderBasket(dragOver, idx); setDragOver(null); }}
                >
                  <div className="pic"><video className="th" src={`${creation.video}#t=0.5`} muted preload="metadata" />
                    <span className="n">{idx + 1}</span>
                    <button type="button" className="rm" aria-label={EXPORTS_PAPER.remove} onClick={() => removeFromBasket(creation.id)}>×</button>
                  </div>
                  <div className="b-txt">{creation.title}</div>
                </div>
              ))}
            </div>
            <div className="b-act">
              <input className="xtitle" value={stitchTitle} placeholder={EXPORTS_PAPER.titlePlaceholder} onChange={(e) => setStitchTitle(e.target.value)} />
              <button type="button" className="pill red" disabled={basketIds.length < 2 || stitching} onClick={handleStitch}>
                ✂ {stitching ? EXPORTS_PAPER.stitchingNow : EXPORTS_PAPER.stitch}
              </button>
              <button type="button" className="pill line" disabled={batchDownloading} onClick={handleDownloadAll}>
                ⬇ {batchDownloading ? EXPORTS_PAPER.downloading : EXPORTS_PAPER.downloadAll(basketIds.length)}
              </button>
            </div>
          </div>
        )}

        {query && filteredCreations.length === 0 && <div className="meta">{LIBRARY_NO_MATCHES}</div>}
        <div className={paperView === "list" ? "xlist" : "grid"} style={{ ["--ccl-card" as any]: `${paperCardW}px` }}>
          {filteredCreations.map((creation) => {
            const video = isVideoFile(creation.ext) && !!creation.ext;
            const inBasket = basketIds.includes(creation.id);
            const playingNow = activeCreationId === creation.id;
            return (
              <div key={creation.id} className={`card xcard${inBasket ? " picked" : ""}${creation.id === newlyStitchedId ? " fresh" : ""}`}>
                <div className="pic">
                  {isNoCherry(creation) ? (
                    <div className="empty xnone">{EXPORT_NO_CHERRY}</div>
                  ) : isProcessing(creation) ? (
                    <div className="empty"><span className="xspin" /></div>
                  ) : isGifFile(creation.ext) ? (
                    <img className="th" src={creation.video} alt="" />
                  ) : video ? (
                    playingNow
                      ? <video className="th" src={creation.video} controls autoPlay playsInline onEnded={() => setActiveCreationId(null)} />
                      : <video className="th" src={`${creation.video}#t=0.5`} muted preload="metadata" onClick={() => handleClipClick(creation)} style={{ cursor: "pointer" }} />
                  ) : isSubtitleFile(creation.ext) ? (
                    <SubtitleText url={creation.video} onCopy={handleCopyToClipboard} />
                  ) : (
                    <div className="empty xfile">.{(creation.ext || "").toUpperCase()}</div>
                  )}
                  {video && !playingNow && <button type="button" className="xplay" aria-label={EXPORTS_PAPER.play} onClick={() => handleClipClick(creation)}>▶</button>}
                  {video && (
                    <button
                      type="button"
                      className={`pick${inBasket ? " on" : ""}`}
                      title={inBasket ? EXPORTS_PAPER.unpick : EXPORTS_PAPER.pick}
                      onClick={() => toggleBasket(creation.id, creation.ext)}
                    >
                      {inBasket ? basketIds.indexOf(creation.id) + 1 : "+"}
                    </button>
                  )}
                  {(creation as any).duration_seconds ? <span className="dur">{fmtLength((creation as any).duration_seconds)}</span> : null}
                </div>
                <div className="body">
                  <div className="xtags"><KindTag kind={creation.kind} ext={creation.ext} />{(creation.description ?? "").startsWith("Stitched from") && <span className="xstitched">{EXPORTS_PAPER.stitched}</span>}</div>
                  <div className="ttl" title={creation.title}>{creation.title}</div>
                  <div className="meta">{formatDateAdded(creation.created)}</div>
                  {isProcessing(creation) || isNoCherry(creation) ? (
                    <div className="xrow">
                      <span className="meta">{isNoCherry(creation) ? (creation.status === "cancelled" ? EXPORT_CANCELLED_TEXT : EXPORT_FAILED_TEXT) : EXPORTS_PAPER.making}</span>
                      {isNoCherry(creation)
                        ? <button type="button" className="pill line" onClick={() => handleDeleteClick(creation.id, creation.title)}>{EXPORT_DELETE_LABEL}</button>
                        : <button type="button" className="pill line" title={EXPORT_GIVE_UP_TITLE} onClick={() => handleGiveUp(creation)}>{EXPORT_GIVE_UP_LABEL}</button>}
                    </div>
                  ) : (
                    <div className="xrow">
                      {isSubtitleFile(creation.ext) ? (
                        <button type="button" className="pill line" onClick={() => handleCopyToClipboard(creation.video)}>⧉ {EXPORTS_PAPER.copyText}</button>
                      ) : null}
                      <button type="button" className="pill red" disabled={isDownloadingId(creation.id)} onClick={() => download(creation.id, creation.video, creation.title, creation.ext || "mp4")}>
                        ⬇ {EXPORTS_PAPER.download}
                      </button>
                      {hasAuto && isPostable(creation) ? (
                        <AutoPostButton creationId={creation.id} state={creation.auto_state} onDone={markPosted} onMessage={showPostMessage} />
                      ) : null}
                      <span style={{ flex: 1 }} />
                      <RowActions
                        videoId={creation.id}
                        videoTitle={creation.title}
                        videoUrl={creation.video}
                        onDownload={(id, url) => download(id, url, creation.title, creation.ext || "mp4")}
                        isDownloading={isDownloadingId(creation.id)}
                        onDelete={handleDeleteClick}
                        onRename={(id, title) => handleRenameClick(id, title, creation.description)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {dialogs}
      </div>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }} style={{ ["--cc-clipw" as any]: `${cardWidth}px` }}>
    <LibraryTabLayout
      loading={loading}
      error={error}
      isEmpty={creations.length === 0}
      emptyMessage={EXPORTS_EMPTY_STATE_MESSAGE}
    >
      {/* Toolbar: category filter · view toggle · thumbnail size slider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 24px", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: "4px", background: "#1a1a1a", borderRadius: "8px", padding: "3px" }}>
          {(["all", "auto", "manual"] as const).map((key) => (
            <Box
              key={key}
              onClick={() => setCategoryFilter(key)}
              sx={{ cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: categoryFilter === key ? "#fff" : "#888", background: categoryFilter === key ? "#e53935" : "transparent", borderRadius: "6px", padding: "5px 12px", whiteSpace: "nowrap", transition: "all 0.15s" }}
            >
              {key === "all" ? "All" : key === "auto" ? "🍒 CherryClipz" : "Manual"}
            </Box>
          ))}
        </Box>
        <input
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={LIBRARY_SEARCH_PLACEHOLDER}
          style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: "8px", color: "#fff", fontFamily: "Outfit, sans-serif", fontSize: "13px", padding: "7px 12px", width: "220px", outline: "none" }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#888" }}>{LIBRARY_SORT_LABEL}</Typography>
          <select
            value={sortChoice}
            onChange={(e) => setSortChoice(e.target.value as ExportSort)}
            style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: "8px", color: "#fff", fontFamily: "Outfit, sans-serif", fontSize: "13px", padding: "6px 8px", outline: "none", cursor: "pointer" }}
          >
            {EXPORT_SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#888" }}>Size</Typography>
          <input type="range" min={160} max={380} step={10} value={cardWidth} onChange={(e) => setCardWidth(Number(e.target.value))} style={{ width: "120px", accentColor: "#e53935" }} />
        </Box>
        <Box sx={{ display: "flex", gap: "4px", background: "#1a1a1a", borderRadius: "8px", padding: "3px" }}>
          {(["list", "grid"] as const).map((key) => (
            <Box
              key={key}
              onClick={() => setViewMode(key)}
              sx={{ cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: viewMode === key ? "#fff" : "#888", background: viewMode === key ? "#e53935" : "transparent", borderRadius: "6px", padding: "5px 12px", whiteSpace: "nowrap", transition: "all 0.15s" }}
            >
              {key === "list" ? "☰ List" : "▦ Grid"}
            </Box>
          ))}
        </Box>
      </Box>

      {viewMode === "list" ? (
      <>
      <LibraryTable>
        <thead>
          <tr>
            <th style={{ width: "42px", borderBottom: "1px solid #2a2a2a" }} />
            {exportsTableHeader.map((column) => (
              <th
                key={column.field}
                style={{
                  width: column.field === "clip" ? "var(--cc-clipw, 138px)" : (typeof column.width === "number" ? `${column.width}px` : column.width),
                  padding: column.padding ?? "12px 24px",
                  textAlign: column.align as any,
                  borderBottom: "1px solid #2a2a2a",
                  cursor: column.sort ? "pointer" : "default",
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor: "transparent",
                  verticalAlign: "middle",
                }}
                onClick={() => { if (column.sort) { setSortChoice("newest"); handleSortClick(column.field); } }}
              >
                {column.title || ""}
              </th>
            ))}
          </tr>
        </thead>
      </LibraryTable>
      {/* Stitch-in-progress banner — memoized so polling re-renders don't restart the spinner */}
      {pendingStitch && <StitchBanner title={pendingStitch} onDismiss={() => setPendingStitch(null)} />}
      {query && filteredCreations.length === 0 && (
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", color: "#888", padding: "24px" }}>{LIBRARY_NO_MATCHES}</Typography>
      )}

      <TableVirtuoso
        data={filteredCreations}
        style={{ flex: 1, width: "100%", overflow: "auto" }}
        context={{ newlyStitchedId }}
        computeItemKey={(index) => filteredCreations[index]?.id ?? index}
        components={EXPORTS_VIRTUOSO_COMPONENTS}
        itemContent={(index) => {
          const creation = filteredCreations[index];

          // No file yet: still processing (spinner + "Give up"), or failed / given up on
          // ("no cherry" stamp + Delete). The poll above flips a processing row when it's ready.
          if (!creation.ext) {
            const noCherry = isNoCherry(creation);
            return (
              <>
                <td style={{ width: "42px", verticalAlign: "top", paddingTop: "12px" }} />
                <td style={{ width: exportsColumnWidths[1], verticalAlign: "top" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {noCherry ? (
                      <NoCherryStamp width="138px" height="78px" />
                    ) : (
                      <Box sx={{ width: "138px", height: "78px", borderRadius: "8px", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Box sx={{ width: "22px", height: "22px", borderRadius: "50%", border: "3px solid #e53935", borderTopColor: "transparent", animation: "spin 0.9s linear infinite" }} />
                      </Box>
                    )}
                  </Box>
                </td>
                <td style={{ width: exportsColumnWidths[2], verticalAlign: "top" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", paddingLeft: "24px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 500, color: noCherry ? colors.neutral[500] : colors.neutral.white, lineHeight: "20px" }}>
                        {creation.title}
                      </Typography>
                      <KindTag kind={creation.kind} />
                    </Box>
                    <SettingsLine kind={creation.kind} settings={creation.settings} />
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 400, color: noCherry ? colors.neutral[500] : "#e53935", lineHeight: "20px" }}>
                      {noCherry ? (creation.status === "cancelled" ? EXPORT_CANCELLED_TEXT : EXPORT_FAILED_TEXT) : EXPORT_PROCESSING_TEXT}
                    </Typography>
                    {/* Right under the message so it's on screen however wide the table is. */}
                    <Box sx={{ mt: "6px" }}>
                      {noCherry ? (
                        <button className="cc-export-action" onClick={() => handleDeleteClick(creation.id, creation.title)}>{EXPORT_DELETE_LABEL}</button>
                      ) : (
                        <button className="cc-export-action" title={EXPORT_GIVE_UP_TITLE} onClick={() => handleGiveUp(creation)}>{EXPORT_GIVE_UP_LABEL}</button>
                      )}
                    </Box>
                  </Box>
                </td>
                <td style={{ width: exportsColumnWidths[3], verticalAlign: "top" }}>
                  <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral.white, lineHeight: "20px" }}>
                      {formatDateAdded(creation.created)}
                    </Typography>
                  </Box>
                </td>
                <td style={{ width: exportsColumnWidths[4], verticalAlign: "top" }}>
                  <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral[500], lineHeight: "20px" }}>—</Typography>
                  </Box>
                </td>
                <td style={{ width: exportsColumnWidths[5], verticalAlign: "top" }}>
                  <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral[500], lineHeight: "20px" }}>—</Typography>
                  </Box>
                </td>
                <td style={{ width: exportsColumnWidths[6], verticalAlign: "top" }}>
                  <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral[500], lineHeight: "20px" }}>—</Typography>
                  </Box>
                </td>
                <td style={{ verticalAlign: "top" }} />
              </>
            );
          }

          const isActive = activeCreationId === creation.id;
          const inBasket = basketIds.includes(creation.id);
          const canSelect = isVideoFile(creation.ext);
          return (
            <>
              {/* Cherry checkbox */}
              <td style={{ width: "42px", verticalAlign: "top", paddingTop: "12px", textAlign: "center" }}>
                {canSelect && (
                  <div
                    onClick={() => toggleBasket(creation.id, creation.ext)}
                    title={inBasket ? "Remove from basket" : basketIds.length >= MAX_STITCH ? `Select up to ${MAX_STITCH} clips` : "Add to basket"}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", width: "24px", height: "24px" }}
                  >
                    {inBasket ? (
                      <svg
                        key={`cherry-${creation.id}`}
                        className="cherry-checkbox-icon"
                        width="22" height="22" viewBox="0 0 25 40" fill="none" xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M15.351 26.6182C15.1147 26.3986 14.7192 26.3179 14.4266 26.2049C14.0923 26.0773 13.7579 25.9482 13.4219 25.8206C12.6631 25.53 11.9027 25.2377 11.1439 24.9471C11.0651 24.9164 10.9799 24.9294 10.9044 24.9681C10.7886 25.0069 10.7002 25.1053 10.7002 25.262C10.7002 25.2668 10.7002 25.27 10.7002 25.2749C10.7002 25.2797 10.7002 25.2829 10.7002 25.2878C10.795 26.862 10.7275 28.4362 10.9622 30.0008C10.9622 30.0202 10.9655 30.0411 10.9703 30.0605C10.9703 30.0654 10.9703 30.0702 10.9719 30.075C10.9719 30.0799 10.9751 30.0847 10.9767 30.0896V30.0928C10.9992 30.1848 11.0683 30.2527 11.1535 30.293C11.1632 30.2979 11.1728 30.3011 11.1825 30.3043C11.1889 30.3075 11.1953 30.3108 11.2034 30.3124C11.2918 30.3431 11.3883 30.3431 11.467 30.2946C11.4718 30.293 11.4751 30.2898 11.4799 30.2882C11.4815 30.2882 11.4847 30.2866 11.4863 30.2849C11.4911 30.2817 11.4944 30.2785 11.4992 30.2753C11.512 30.2672 11.5249 30.2575 11.5362 30.2478C12.5441 29.5132 13.5601 28.7914 14.5697 28.06C14.9684 27.771 15.9764 27.1995 15.351 26.6182Z" fill="#ffffff"/>
                        <path d="M12.8272 39.9983C10.8354 39.9983 7.77289 39.3202 5.15409 37.415C2.94041 35.8053 0.250872 32.7295 0.0129453 27.2237C-0.141386 23.6684 1.08844 20.331 3.47574 17.8268C5.58332 15.6165 8.32269 14.349 10.9945 14.349C14.8062 14.349 16.4331 15.5712 16.7209 15.815L14.602 18.3225C14.602 18.3225 14.6181 18.337 14.6519 18.3629C14.6422 18.3564 13.6182 17.6379 10.9962 17.6379C9.24064 17.6379 7.31472 18.5598 5.84376 20.1018C4.0802 21.9505 3.17351 24.4305 3.28765 27.0816C3.63007 34.9802 10.292 36.711 12.8304 36.711C17.8173 36.711 21.7238 33.4399 21.7238 29.2629C21.7238 27.1914 21.2447 25.9498 21.2399 25.9369C20.8942 25.0973 21.2929 24.1415 22.1289 23.7943C22.9648 23.4472 23.9246 23.8525 24.2702 24.692C24.3442 24.8729 24.9968 26.5359 24.9968 29.2629C24.9968 35.2837 19.6515 40 12.8288 40L12.8272 39.9983Z" fill="#E61D50"/>
                        <path d="M15.3866 21.7971C21.0502 16.5675 24.46 9.27278 24.9953 1.58088C25.1416 -0.530996 21.8669 -0.522923 21.7206 1.58088C21.2448 8.41866 18.0826 14.8431 13.07 19.4721C11.5155 20.9075 13.8353 23.2293 15.385 21.7971H15.3866Z" fill="#E61D50"/>
                      </svg>
                    ) : (
                      <div className="cherry-checkbox-ring" />
                    )}
                  </div>
                )}
              </td>
              <td style={{ width: exportsColumnWidths[1], verticalAlign: "top" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {isActive ? (
                    <InlineVideoPlayer videoUrl={creation.video} heightPx={listThumbHeight} onStop={() => setActiveCreationId(null)} />
                  ) : (
                    <VideoThumbnail
                      videoUrl={creation.video}
                      thumbnailUrl={isGifFile(creation.ext) ? creation.video : undefined}
                      blackBackground={!canSelect && !isGifFile(creation.ext)}
                      heightPx={listThumbHeight}
                      onClick={canSelect ? () => handleClipClick(creation) : undefined}
                      onCopyToClipboard={isSubtitleFile(creation.ext) ? () => handleCopyToClipboard(creation.video) : undefined}
                    />
                  )}
                </Box>
              </td>
              <td style={{ width: exportsColumnWidths[2], verticalAlign: "top", cursor: CURSOR_POINTER }} onClick={() => handleClipClick(creation)}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", paddingLeft: "24px" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 500, color: colors.neutral.white, lineHeight: "20px", flex: "1 1 auto", minWidth: "120px" }}>
                      {creation.title}
                    </Typography>
                    {!(creation.description ?? "").startsWith("Stitched from") && <KindTag kind={creation.kind} ext={creation.ext} />}
                    {(creation.description ?? "").startsWith("Stitched from") && (
                      <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, color: "#fff", background: "#7c3aed", borderRadius: "4px", px: "5px", py: "1px", letterSpacing: "0.5px", textTransform: "uppercase", flexShrink: 0 }}>
                        Stitched
                      </Box>
                    )}
                  </Box>
                  <SettingsLine kind={creation.kind} settings={creation.settings} />
                  <Typography
                    onClick={(e) => { e.stopPropagation(); handleRenameClick(creation.id, creation.title, creation.description ?? ""); }}
                    sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: creation.description ? colors.neutral[600] : colors.neutral[500], lineHeight: "20px", fontStyle: creation.description ? "normal" : "italic", cursor: "pointer", "&:hover": { color: colors.neutral.white } }}
                  >
                    {creation.description || "Write a description here."}
                  </Typography>
                </Box>
              </td>
              <td style={{ width: exportsColumnWidths[3], verticalAlign: "top" }}>
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral.white, lineHeight: "20px" }}>
                    {formatDateAdded(creation.created)}
                  </Typography>
                </Box>
              </td>
              <td style={{ width: exportsColumnWidths[4], verticalAlign: "top" }}>
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral.white, lineHeight: "20px" }}>
                    {fmtLength((creation as any).duration_seconds)}
                  </Typography>
                </Box>
              </td>
              <td style={{ width: exportsColumnWidths[5], verticalAlign: "top" }}>
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral.white, lineHeight: "20px" }}>
                    {fmtSecs((creation as any).processing_time_seconds)}
                  </Typography>
                </Box>
              </td>
              <td style={{ width: exportsColumnWidths[6], verticalAlign: "top" }}>
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 400, color: colors.neutral.white, lineHeight: "20px", textTransform: "uppercase" }}>
                    {creation.ext || "—"}
                  </Typography>
                </Box>
              </td>
              <td style={{ verticalAlign: "top" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                  {hasAuto && isPostable(creation) ? (
                    <AutoPostButton creationId={creation.id} state={creation.auto_state} onDone={markPosted} onMessage={showPostMessage} />
                  ) : null}
                  <RowActions
                    videoId={creation.id}
                    videoTitle={creation.title}
                    videoUrl={creation.video}
                    onDownload={(id, url) => download(id, url, creation.title, creation.ext || "mp4")}
                    isDownloading={isDownloadingId(creation.id)}
                    onDelete={handleDeleteClick}
                    onRename={(id, title) => handleRenameClick(id, title, creation.description)}
                    downloadInline={true}
                  />
                </Box>
              </td>
            </>
          );
        }}
      />
      </>
      ) : (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", padding: "16px 24px", overflowY: "auto", alignContent: "flex-start" }}>
        {filteredCreations.map((creation) => {
          const canSelect = isVideoFile(creation.ext);
          const isActive = activeCreationId === creation.id;
          const thumbH = Math.round((cardWidth * 9) / 16);
          return (
            <Box key={creation.id} sx={{ width: `${cardWidth}px`, background: "#161616", borderRadius: "10px", overflow: "hidden", border: creation.id === newlyStitchedId ? "1px solid #e53935" : "1px solid #2a2a2a", display: "flex", flexDirection: "column" }}>
              <Box sx={{ width: "100%", height: `${thumbH}px`, background: "#000", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {isNoCherry(creation) ? (
                  <NoCherryStamp width="100%" height="100%" />
                ) : !creation.ext ? (
                  <Box sx={{ width: "26px", height: "26px", borderRadius: "50%", border: "3px solid #e53935", borderTopColor: "transparent", animation: "spin 0.9s linear infinite" }} />
                ) : isGifFile(creation.ext) ? (
                  <img src={creation.video} alt={creation.title} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
                ) : isActive ? (
                  <InlineVideoPlayer videoUrl={creation.video} heightPx={thumbH} onStop={() => setActiveCreationId(null)} />
                ) : (
                  <Box onClick={canSelect ? () => handleClipClick(creation) : undefined} sx={{ width: "100%", height: "100%", cursor: canSelect ? "pointer" : "default" }}>
                    <video src={creation.video} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000", pointerEvents: "none" }} preload="metadata" muted />
                  </Box>
                )}
                {isAutoCut(creation) && (
                  <Box sx={{ position: "absolute", top: "6px", left: "8px", fontSize: "14px", lineHeight: 1, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }}>🍒</Box>
                )}
                {creation.ext && isVideoFile(creation.ext) && (
                  <Box
                    onClick={(e) => { e.stopPropagation(); toggleBasket(creation.id, creation.ext); }}
                    title={basketIds.includes(creation.id) ? "Remove from basket" : basketIds.length >= MAX_STITCH ? `Select up to ${MAX_STITCH} clips` : "Add to basket"}
                    sx={{ position: "absolute", top: "6px", right: "8px", cursor: "pointer", width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "rgba(0,0,0,0.45)" }}
                  >
                    {basketIds.includes(creation.id) ? (
                      <svg width="16" height="16" viewBox="0 0 25 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.8272 39.9983C10.8354 39.9983 7.77289 39.3202 5.15409 37.415C2.94041 35.8053 0.250872 32.7295 0.0129453 27.2237C-0.141386 23.6684 1.08844 20.331 3.47574 17.8268C5.58332 15.6165 8.32269 14.349 10.9945 14.349C14.8062 14.349 16.4331 15.5712 16.7209 15.815L14.602 18.3225C14.602 18.3225 14.6181 18.337 14.6519 18.3629C14.6422 18.3564 13.6182 17.6379 10.9962 17.6379C9.24064 17.6379 7.31472 18.5598 5.84376 20.1018C4.0802 21.9505 3.17351 24.4305 3.28765 27.0816C3.63007 34.9802 10.292 36.711 12.8304 36.711C17.8173 36.711 21.7238 33.4399 21.7238 29.2629C21.7238 27.1914 21.2447 25.9498 21.2399 25.9369C20.8942 25.0973 21.2929 24.1415 22.1289 23.7943C22.9648 23.4472 23.9246 23.8525 24.2702 24.692C24.3442 24.8729 24.9968 26.5359 24.9968 29.2629C24.9968 35.2837 19.6515 40 12.8288 40L12.8272 39.9983Z" fill="#E61D50"/>
                        <path d="M15.3866 21.7971C21.0502 16.5675 24.46 9.27278 24.9953 1.58088C25.1416 -0.530996 21.8669 -0.522923 21.7206 1.58088C21.2448 8.41866 18.0826 14.8431 13.07 19.4721C11.5155 20.9075 13.8353 23.2293 15.385 21.7971H15.3866Z" fill="#E61D50"/>
                      </svg>
                    ) : (
                      <Box sx={{ width: "16px", height: "16px", borderRadius: "50%", border: "2px solid #fff" }} />
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: "4px" }}>
                <Typography title={creation.title} sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {creation.title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                  {creation.ext || isNoCherry(creation) ? (
                    <KindTag kind={creation.kind} ext={creation.ext} settings={creation.settings} />
                  ) : (
                    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#e53935" }}>Processing…</Typography>
                  )}
                  {creation.ext ? <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#aaa" }}>· {fmtLength(creation.duration_seconds)}</Typography> : null}
                  {creation.processing_time_seconds ? <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#777" }}>· ⏱ {fmtSecs(creation.processing_time_seconds)}</Typography> : null}
                </Box>
                <SettingsLine kind={creation.kind} settings={creation.settings} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "2px" }}>
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#666" }}>{formatDateAdded(creation.created)}</Typography>
                  {!creation.ext && (
                    isNoCherry(creation) ? (
                      <button className="cc-export-action" onClick={() => handleDeleteClick(creation.id, creation.title)}>{EXPORT_DELETE_LABEL}</button>
                    ) : (
                      <button className="cc-export-action" title={EXPORT_GIVE_UP_TITLE} onClick={() => handleGiveUp(creation)}>{EXPORT_GIVE_UP_LABEL}</button>
                    )
                  )}
                  {hasAuto && isPostable(creation) ? (
                    <AutoPostButton creationId={creation.id} state={creation.auto_state} onDone={markPosted} onMessage={showPostMessage} />
                  ) : null}
                  {creation.ext && (
                    <RowActions
                      videoId={creation.id}
                      videoTitle={creation.title}
                      videoUrl={creation.video}
                      onDownload={(id, url) => download(id, url, creation.title, creation.ext || "mp4")}
                      isDownloading={isDownloadingId(creation.id)}
                      onDelete={handleDeleteClick}
                      onRename={(id, title) => handleRenameClick(id, title, creation.description)}
                      downloadInline={true}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      )}

      <ConfirmModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        title={DELETE_CONFIRM_TITLE}
        message={creationToDelete ? getDeleteConfirmMessage(creationToDelete.title) : ""}
        confirmLabel={BUTTON_CONFIRM}
        cancelLabel={BUTTON_CANCEL}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteModalClose}
        loading={isDeleting}
      />
      <RenameDialog
        open={renameDialogOpen}
        currentTitle={creationToRename?.title || ""}
        currentDescription={creationToRename?.description ?? ""}
        onClose={() => { setRenameDialogOpen(false); setCreationToRename(null); }}
        onConfirm={handleRenameConfirm}
        loading={isRenaming}
      />
    </LibraryTabLayout>

    {/* Clip Basket — sticky to bottom of viewport, always visible when clips are selected */}
    {basketIds.length > 0 && (
      <Box sx={{
        position: "sticky",
        bottom: 0,
        background: "#1a1a1a",
        borderRadius: "12px 12px 0 0",
        borderTop: "1px solid #444",
        borderLeft: "1px solid #444",
        borderRight: "1px solid #444",
        padding: "12px 20px",
        marginTop: "12px",
        zIndex: 100,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.4)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: "#e53935" }}>
            Clip Basket ({basketIds.length} clip{basketIds.length !== 1 ? "s" : ""})
          </Typography>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#666" }}>
            Drag to reorder · Select 2 clips to stitch together
          </Typography>
          <Box sx={{ flex: 1 }} />
          <input
            type="text"
            value={stitchTitle}
            onChange={(e) => setStitchTitle(e.target.value)}
            placeholder="Stitched video title..."
            style={{
              background: "#2a2a2a", border: "1px solid #444", borderRadius: "6px",
              color: "#fff", fontFamily: "Outfit, sans-serif", fontSize: "13px",
              padding: "6px 10px", width: "220px", outline: "none",
            }}
          />
          <button
            onClick={handleDownloadAll}
            disabled={batchDownloading}
            style={{
              background: batchDownloading ? "#444" : "#1565c0",
              color: "#fff", border: "none", borderRadius: "6px",
              padding: "7px 16px", fontFamily: "Outfit, sans-serif",
              fontSize: "13px", fontWeight: 600,
              cursor: batchDownloading ? "not-allowed" : "pointer",
              transition: "background 0.2s",
            }}
          >
            {batchDownloading ? "Downloading…" : `⬇ Download All (${basketIds.length})`}
          </button>
          <button
            onClick={handleStitch}
            disabled={basketIds.length < 2 || stitching}
            style={{
              background: basketIds.length >= 2 ? "#e53935" : "#444",
              color: "#fff", border: "none", borderRadius: "6px",
              padding: "7px 16px", fontFamily: "Outfit, sans-serif",
              fontSize: "13px", fontWeight: 600, cursor: basketIds.length >= 2 ? "pointer" : "not-allowed",
              transition: "background 0.2s",
            }}
          >
            {stitching ? "Stitching…" : "✂ Stitch & Export"}
          </button>
          <button
            onClick={() => setBasketIds([])}
            style={{
              background: "transparent", color: "#888", border: "1px solid #444",
              borderRadius: "6px", padding: "6px 12px", fontFamily: "Outfit, sans-serif",
              fontSize: "12px", cursor: "pointer",
            }}
          >
            Clear
          </button>
        </Box>
        <Box sx={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "4px" }}>
          {basketCreations.map((creation, idx) => (
            <Box
              key={creation.id}
              draggable
              onDragStart={() => { dragItem.current = idx; }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(idx); }}
              onDrop={() => {
                if (dragItem.current !== null && dragItem.current !== idx) {
                  reorderBasket(dragItem.current, idx);
                }
                dragItem.current = null;
                setDragOver(null);
              }}
              onDragEnd={() => { dragItem.current = null; setDragOver(null); }}
              sx={{
                position: "relative", flexShrink: 0, width: "80px",
                border: dragOver === idx ? "2px solid #e53935" : "2px solid #333",
                borderRadius: "6px", overflow: "hidden", cursor: "grab",
                transition: "border-color 0.15s",
                "&:hover .basket-remove": { opacity: 1 },
              }}
            >
              <Box sx={{
                position: "absolute", top: "2px", left: "4px", zIndex: 2,
                background: "#e53935", color: "#fff", borderRadius: "50%",
                width: "18px", height: "18px", fontSize: "11px", fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "Outfit, sans-serif",
              }}>
                {idx + 1}
              </Box>
              <video
                src={creation.video}
                style={{ width: "80px", height: "52px", objectFit: "contain", backgroundColor: "#000", display: "block", pointerEvents: "none" }}
                preload="metadata"
                muted
              />
              <Typography sx={{
                fontFamily: "Outfit, sans-serif", fontSize: "10px", color: "#ccc",
                padding: "2px 4px", whiteSpace: "nowrap", overflow: "hidden",
                textOverflow: "ellipsis", background: "#111",
              }}>
                {creation.title}
              </Typography>
              <Box
                className="basket-remove"
                onClick={() => removeFromBasket(creation.id)}
                sx={{
                  position: "absolute", top: "2px", right: "2px", zIndex: 2,
                  background: "#00000088", color: "#fff", borderRadius: "50%",
                  width: "16px", height: "16px", fontSize: "11px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", opacity: 0, transition: "opacity 0.15s",
                }}
              >
                ×
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    )}
    </Box>
  );
};

export default observer(ExportsTab);
